// src/components/Sidebar.tsx

/**
 * Persistent sidebar navigation for the Quantik Mind enterprise frontend.
 *
 * This component is responsible for:
 * - rendering the main application navigation
 * - highlighting the active route
 * - blocking project-dependent routes when no project is selected
 * - displaying the currently active project
 * - exposing project creation from the dashboard view
 *
 * The sidebar remains mounted across authenticated pages and acts as the main
 * entry point for project-scoped navigation.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Tooltip,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ScienceIcon from '@mui/icons-material/Science';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocation, useNavigate } from 'react-router-dom';

import { fetchAllRuns, type RunSummary } from '../api/runs';
import { useAuth } from '../context/authContext';
import { useProject } from '../context/ProjectContext';
import Button from './common/ActionButton';
import CreateProjectButton from './CreateProjectButton';
import ProjectRequiredModal from './ProjectRequiredModal';
import SupportRequestModal from './SupportRequestModal';

/**
 * Sidebar drawer width in pixels.
 */
const drawerWidth = {
  xs: 116,
  sm: 220,
};

/**
 * Navigation item definition used by the sidebar menu.
 */
interface MenuItem {
  /** User-facing menu label. */
  label: string;

  /** Visual icon associated with the route. */
  icon: React.ReactElement;

  /** Route path navigated when the item is selected. */
  path: string;

  /** Optional badge shown next to the item label. */
  badge?: string | null;
}

/**
 * Static menu configuration for the enterprise application shell.
 */
const menuItems: MenuItem[] = [
  { label: 'Dashboard', icon: <HomeIcon />, path: '/', badge: null },
  {
    label: 'Selection',
    icon: <ScienceIcon />,
    path: '/quantum',
    badge: null,
  },
  {
    label: 'Metrics & Results',
    icon: <BarChartIcon />,
    path: '/results',
    badge: null,
  },
  { label: 'Settings', icon: <SettingsIcon />, path: '/settings', badge: null },
];

/**
 * Render the persistent enterprise sidebar.
 *
 * @returns Sidebar navigation component.
 */
export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authData } = useAuth();
  const { currentProject, setCurrentProject } = useProject();

  const [modalOpen, setModalOpen] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportSuccess, setSupportSuccess] = useState('');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  /**
   * Latest run is currently loaded for sidebar-level context only.
   *
   * The current UI does not display it yet, but the existing behavior is
   * preserved to avoid changing runtime behavior while cleaning the file.
   */
  const [, setLastRun] = useState<RunSummary | null>(null);

  /**
   * The create-project shortcut is displayed only on the dashboard route.
   */
  const isHomePage = location.pathname === '/';
  const isOnboardingLocked =
    location.pathname === '/onboarding' && !onboardingComplete;

  useEffect(() => {
    if (!authData?.organizationId) {
      setOnboardingComplete(false);
      return undefined;
    }

    const readOnboardingStatus = () => {
      setOnboardingComplete(
        localStorage.getItem(`onboarding_complete_${authData.organizationId}`) === 'true'
      );
    };

    readOnboardingStatus();
    window.addEventListener('quantik:onboarding-status', readOnboardingStatus);
    return () => {
      window.removeEventListener('quantik:onboarding-status', readOnboardingStatus);
    };
  }, [authData?.organizationId, location.pathname]);

  useEffect(() => {
    /**
     * Load the latest available run for the currently selected project.
     */
    const loadLastRun = async () => {
      if (!currentProject) {
        setLastRun(null);
        return;
      }

      try {
        const response = await fetchAllRuns(currentProject.id, 1, 0);

        if (response.data.length > 0) {
          setLastRun(response.data[0]);
        } else {
          setLastRun(null);
        }
      } catch (error) {
        console.error('Failed to load latest run for sidebar:', error);
        setLastRun(null);
      }
    };

    void loadLastRun();
  }, [currentProject]);

  /**
   * Navigate to the selected route.
   *
   * Routes other than the dashboard require an active project. When no project
   * is selected, the user is prompted through a dedicated modal instead.
   *
   * @param path - Destination route path.
   */
  const handleMenuClick = (path: string) => {
    if (isOnboardingLocked) {
      return;
    }

    if (!currentProject && path !== '/') {
      setModalOpen(true);
      return;
    }

    navigate(path);
  };

  /**
   * Update the active project after successful project creation.
   *
   * A full page reload is currently preserved to align with existing behavior
   * and force dependent views to refresh.
   *
   * @param projectId - Identifier of the newly created project.
   * @param projectName - Display name of the newly created project.
   */
  const handleProjectCreated = (projectId: string, projectName: string) => {
    setCurrentProject({ id: projectId, name: projectName });

    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: '#0D1117',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            pt: 8,
            overflowX: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Primary application navigation. */}
          <List sx={{ px: 2, py: 1 }}>
            {menuItems.map(({ label, icon, path, badge }) => {
              const isActive = location.pathname === path;
              const isDisabled = isOnboardingLocked;
              const isHovered = !isDisabled && hoveredItem === path;

              return (
                <ListItem key={label} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleMenuClick(path)}
                    onMouseEnter={() => setHoveredItem(path)}
                    onMouseLeave={() => setHoveredItem(null)}
                    disabled={isDisabled}
                    sx={{
                      borderRadius: 1.5,
                      py: 1,
                      px: 1.5,
                      transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                      opacity: isDisabled ? 0.42 : 1,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      bgcolor: isActive ? '#0A0E14' : 'transparent',
                      border: isActive
                        ? '1px solid #39ff14'
                        : '1px solid transparent',
                      boxShadow: isActive
                        ? '0 0 0 1px rgba(57, 255, 20, 0.10), 0 0 18px rgba(57, 255, 20, 0.14)'
                        : 'none',
                      '&:hover': {
                        bgcolor: isDisabled ? 'transparent' : '#0A0E14',
                        borderColor: isDisabled ? 'transparent' : '#39ff14',
                        boxShadow: isDisabled
                          ? 'none'
                          : '0 0 0 1px rgba(57, 255, 20, 0.10), 0 0 18px rgba(57, 255, 20, 0.14)',
                      },
                      '&.Mui-focusVisible': {
                        outline: '2px solid #39ff14',
                        outlineOffset: 2,
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: 3,
                        height: '100%',
                        bgcolor: '#39ff14',
                        transform: isActive && !isDisabled ? 'scaleY(1)' : 'scaleY(0)',
                        transition: 'transform 0.2s ease',
                        borderRadius: '0 2px 2px 0',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: isActive
                          ? '#39ff14'
                          : isHovered
                          ? '#39ff14'
                          : 'text.secondary',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      {icon}
                    </ListItemIcon>

                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.875rem',
                        color: isActive ? '#39ff14' : '#C9D1D9',
                        noWrap: true,
                        sx: { display: { xs: 'none', sm: 'block' } },
                      }}
                    />

                    {badge && (
                      <Chip
                        label={badge}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          bgcolor: 'rgba(57, 255, 20, 0.2)',
                          color: '#39ff14',
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 2, mx: 2, bgcolor: 'divider' }} />

          {/* Active project summary and project creation actions. */}
          <Box sx={{ px: 2, mt: 'auto', pb: 3 }}>
            {isHomePage && (
              <Box sx={{ mb: 2 }}>
                <CreateProjectButton onProjectCreated={handleProjectCreated} />
              </Box>
            )}

            <Tooltip title="Need help or want to provide feedback?" describeChild>
              <Box sx={{ mb: 2 }}>
                <Button
                  fullWidth
                  allowWrap
                  startIcon={<HelpOutlineIcon />}
                  onClick={() => setSupportModalOpen(true)}
                  sx={{
                    minHeight: 40,
                    px: { xs: 1, sm: 1.5 },
                    py: 0.9,
                    fontSize: { xs: '0.78rem', sm: '0.875rem' },
                  }}
                >
                  Support
                </Button>
              </Box>
            </Tooltip>

            <Typography
              variant="caption"
              sx={{
                color: '#7D8590',
                textTransform: 'uppercase',
                fontWeight: 600,
                letterSpacing: '0.05em',
                fontSize: '0.6875rem',
                mb: 1,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Active Project
            </Typography>

            {currentProject ? (
              <Typography
                variant="body2"
                sx={{
                  color: '#39ff14',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  mb: 2,
                  px: 1.5,
                  py: 1,
                  bgcolor: '#0A0E14',
                  borderRadius: 1.5,
                  border: '1px solid #39ff14',
                  boxShadow: '0 0 0 1px rgba(57, 255, 20, 0.10), 0 0 18px rgba(57, 255, 20, 0.14)',
                  wordBreak: 'break-word',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {currentProject.name}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: '#7D8590',
                  fontStyle: 'italic',
                  fontSize: '0.75rem',
                  px: 1.5,
                  py: 1,
                  mb: 2,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                No project selected
              </Typography>
            )}
          </Box>
        </Box>
      </Drawer>

      <ProjectRequiredModal
        open={modalOpen}
        onSelectProject={() => setModalOpen(false)}
      />
      <SupportRequestModal
        open={supportModalOpen}
        requesterEmail={authData?.email}
        onClose={() => setSupportModalOpen(false)}
        onRequestCreated={(referenceCode) => {
          setSupportModalOpen(false);
          setSupportSuccess(`Request received. Your reference is ${referenceCode}.`);
        }}
      />
      <Snackbar
        open={Boolean(supportSuccess)}
        autoHideDuration={7000}
        onClose={() => setSupportSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSupportSuccess('')}>
          {supportSuccess}
        </Alert>
      </Snackbar>
    </>
  );
}
